import React, { useEffect, useState } from "react";
import { useLanguage } from "../../../../Authentication/LanguageContext";
import ServerUrl from "../../../../../BACKEND";
import "./Animals.css";

interface AnimalItem {
  id: number;
  german: string;
  sentence: string;
  audio: string | null;
  image: string | null;
  tamil: string;
  english: string;
  sinhala: string;
  hindi: string;
  arabic: string;
  turkish: string;
  russian: string;
  chinese: string;
  french: string;
  spanish: string;
  [key: string]: string | number | null;
}

const capitalize = (text: string) =>
  text ? text.charAt(0).toUpperCase() + text.slice(1) : "";

const Animals: React.FC = () => {
  const [data, setData] = useState<AnimalItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [currentlyPlaying, setCurrentlyPlaying] = useState<string | null>(null);
  const { language } = useLanguage();

  useEffect(() => {
    fetch(`${ServerUrl}/api/animals`)
      .then((res) => res.json())
      .then((json) => setData(json))
      .catch((err) => console.error("Error fetching animals:", err))
      .finally(() => setLoading(false));
  }, [language]);

  const getTranslation = (item: AnimalItem) => {
    const map: Record<string, string> = {
      en: "english",
      de: "german",
      ta: "tamil",
      si: "sinhala",
      hi: "hindi",
      ar: "arabic",
      tr: "turkish",
      ru: "russian",
      zh: "chinese",
      fr: "french",
      es: "spanish",
    };
    return capitalize(String(item[map[language] || "english"] || ""));
  };

  const playAudioOrTTS = (item: AnimalItem) => {
    if (currentlyPlaying === item.german) {
      setCurrentlyPlaying(null);
      return;
    }

    setCurrentlyPlaying(item.german);

    if (item.audio) {
      const audio = new Audio(`${ServerUrl}/storage/${item.audio}`);
      audio.play();
      audio.onended = () => setCurrentlyPlaying(null);
      audio.onerror = () => {
        speak(item.german);
      };
    } else {
      speak(item.german);
    }
  };

  const speak = (text: string) => {
    const utterance = new SpeechSynthesisUtterance(text);
    utterance.lang = "de-DE";
    utterance.onend = () => setCurrentlyPlaying(null);
    speechSynthesis.speak(utterance);
  };

  const getImageUrl = (image: string | null) => {
    if (!image) return `${ServerUrl}/storage/A1Image/placeholder.png`;
    if (image.startsWith("http")) return image;
    return `${ServerUrl}/storage/${image}`;
  };

  const titleMap = {
    en: "Animals",
    de: "Tiere",
    ta: "விலங்குகள்",
    si: "සත්වයන්",
    hi: "जानवर",
    ar: "الحيوانات",
    tr: "Hayvanlar",
    ru: "Животные",
    zh: "动物",
    fr: "Animaux",
    es: "Animales",
  };

  const subtitleMap = {
    en: "Animals in German",
    de: "Tiere auf Deutsch",
    ta: "ஜெர்மனில் விலங்குகள்",
    si: "ජර්මානු භාෂාවෙන් සත්වයන්",
    hi: "जर्मन में जानवर",
    ar: "الحيوانات بالألمانية",
    tr: "Almanca'da Hayvanlar",
    ru: "Животные на немецком",
    zh: "德语动物",
    fr: "Animaux en allemand",
    es: "Animales en alemán",
  };

  return (
    <div className="animals-page-container">
      <div className="animals-page-header">
        <div>
          <h1 className="animals-page-title">{titleMap["de"]}</h1>
          <p className="animals-page-subtitle">{subtitleMap[language]}</p>
        </div>
      </div>

      {loading ? (
        <div className="animals-page-loading">Loading...</div>
      ) : (
        <div className="animals-page-grid">
          {data.map((item) => {
            const isPlaying = currentlyPlaying === item.german;
            return (
              <div
                key={item.id}
                className={`animals-page-card ${
                  isPlaying ? "animals-page-playing" : ""
                }`}
                onClick={() => playAudioOrTTS(item)}
              >
                <div className="animals-page-image-wrapper">
                  <img
                    src={getImageUrl(item.image)}
                    alt={item.german}
                    className="animals-page-image"
                    onError={(e) => {
                      const target = e.target as HTMLImageElement;
                      target.src = `${ServerUrl}/storage/A1Image/placeholder.png`;
                    }}
                  />
                </div>
                <div className="animals-page-german-text">
                  {capitalize(item.german)}
                </div>
                <div className="animals-page-translation-text">
                  {getTranslation(item)}
                </div>
                <div className="animals-page-sentence-text">{item.sentence}</div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default Animals;
